import { useCallback, useMemo } from 'react';
import _pick from 'lodash/pick';
import _omit from 'lodash/omit';
import { ProductCategory, ProductSubcategory } from 'api/types/product';
import { IGuestProduct } from 'api/guest_sdk';

interface Props {
  products: Array<IGuestProduct>;
}

export const ALL_PRODUCTS_KEY = 'All products';

const DEFAULT_CATEGORIES = Object.entries(ProductSubcategory)
  .slice(0, -1)
  .reduce(
    (res, [_, val]) => {
      return { ...res, [val]: 0 };
    },
    { [ALL_PRODUCTS_KEY]: 0 },
  );

export default function useCategories({ products }: Props) {
  const categories = useMemo(() => {
    const result = { [ALL_PRODUCTS_KEY]: 0 };

    const normalize = (value) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

    for (const { category, subcategory } of products ?? []) {
      if (!subcategory && !category) continue;

      if (category === ProductCategory.PO && subcategory === 'Add-ons') {
        continue;
      }

      if (
        (category === ProductCategory.LP || category === ProductCategory.POLP) &&
        subcategory === null
      ) {
        const localServicesKey = ProductSubcategory.LocalServices;
        result[localServicesKey] = (result[localServicesKey] || 0) + 1;
        continue;
      }
      const normalizedKey =
        Object.values(ProductSubcategory).find(
          (val) => normalize(val) === normalize(subcategory),
        ) || subcategory;

      result[normalizedKey] = (result[normalizedKey] || 0) + 1;
    }

    return result;
  }, [products]);
  const getOptions = useCallback((categories) => {
    return Object.keys(categories).map((value) => ({
      value,
      label: value === ALL_PRODUCTS_KEY ? value : `${value} (${categories[value]})`,
    }));
  }, []);

  const baseCategories = _pick(categories, Object.keys(DEFAULT_CATEGORIES));
  const otherCategories = _omit(categories, Object.keys(baseCategories));

  return { categories: baseCategories, otherCategories, getOptions };
}
